import React, { useCallback } from 'react';

import type { EmojiProvider } from '@atlaskit/emoji/resource';
import { fg } from '@atlaskit/platform-feature-flags';
import { isPreviewCardAction } from '@atlassian/conversation-assistant-action-helpers/utils';
import {
	isJiraCreatableAction,
	isJiraPreviewCardAction,
} from '@atlassian/conversation-assistant-action-helpers/utils/jira-actions';
import { isJsmCreatableAction } from '@atlassian/conversation-assistant-action-helpers/utils/jsm-actions';
import { type ActionWith } from '@atlassian/conversation-assistant-message-actions-api/types';
import { SmartCreatePreviewComponent } from '@atlassian/conversation-assistant-message-actions/controllers/confluence-aifc-create';
// eslint-disable-next-line @typescript-eslint/ban-ts-comment
// @ts-ignore - this is not a valid package entry point and cannot be resolved when using a modern Typescript 'moduleResolution' setting
import type { SmartCreatePayload } from '@atlassian/conversation-assistant-message-actions/src/controllers/confluence-aifc-create/types';
import type {
	MessageAction as APIMessageAction,
	MessageActionResolutions,
} from '@atlassian/conversation-assistant-service-api/types/message-actions';
import {
	isStoreAssistant,
	type StoreAssistant,
	type StoreChatItem,
} from '@atlassian/conversation-assistant-store/controllers/chat/types';
import { useChatMessagesActions } from '@atlassian/conversation-assistant-store/controllers/chat/use-chat-messages';

import { useUIConfig } from '../../../../common/utils/ui-config/useUIConfig';
import { isEmptyConversation } from '../../../../controllers/chat-store/isEmptyConversation';
import { useChatStoreActions } from '../../../../controllers/chat-store/useChatStoreActions';
import { useConversation } from '../../../../controllers/conversation/useConversation';

import { useResolveActions } from './useResolveActions';

const getLastAssistantMessageId = (messages?: StoreChatItem[]): string => {
	const assistantMessages = messages?.filter(isStoreAssistant);
	const lastAssistantMessage =
		assistantMessages && assistantMessages.length > 0
			? assistantMessages[assistantMessages.length - 1]
			: undefined;

	return lastAssistantMessage?.content?.message?.message?.id ?? '';
};

const getJiraLastAssistantCreatableActionMessageId = (messages?: StoreChatItem[]): string => {
	// We want to get the last message with Jira creatable actions, discarding messages with other types or no actions
	const assistantMessagesWithCreatableActions: StoreAssistant[] = (messages?.filter((msg) => {
		if (!isStoreAssistant(msg)) {
			return false;
		}

		const actions = msg.content?.message?.message?.actions;

		return Boolean(actions?.[0] && isJiraCreatableAction(actions[0]));
	}) ?? []) as StoreAssistant[];

	const lastAssistantMessageWithCreatableActions =
		assistantMessagesWithCreatableActions && assistantMessagesWithCreatableActions.length > 0
			? assistantMessagesWithCreatableActions[assistantMessagesWithCreatableActions.length - 1]
			: undefined;

	return lastAssistantMessageWithCreatableActions?.content?.message?.message?.id ?? '';
};

export const MessageStreamActions = ({
	actions,
	messageId,
	emojiProvider,
	cloudId,
	product,
}: {
	actions: APIMessageAction[];
	messageId: string;
	emojiProvider: Promise<EmojiProvider> | undefined;
	cloudId: string;
	product?: string;
}): React.JSX.Element[] => {
	const { elements, fullScreen, isContentPreviewSupported } = useUIConfig();
	const { conversation } = useConversation();
	const { getMessages } = useChatMessagesActions();
	const { bulkResolveConversationUIActions } = useChatStoreActions();
	const { resolveAction } = useResolveActions(messageId);
	const messages = getMessages(conversation?.id ?? '') ?? [];
	const lastAssistantMessageId = getLastAssistantMessageId(messages);

	// 'preparing' actions are technically previewable, they just render a lot sooner than the content is displayed, so its a weird UX to have a card for something that isn't ready to view yet.
	const previewableActions = actions.filter(
		(action) => isPreviewCardAction(action) && action.status?.toLowerCase() !== 'preparing',
	);

	const conversationId = !isEmptyConversation(conversation?.id) ? conversation?.id : undefined;

	// Only use Atlassian Home preview when:
	// 1. content preview is not supported and
	// 2. AIFC is disabled and
	// 3. preview cards are enabled
	const shouldOpenPreviewInAtlassianHome = !isContentPreviewSupported && !fg('aifc_create_enabled');

	const jiraPreviewableActions = previewableActions.filter((action) =>
		isJiraPreviewCardAction({ key: action?.key }),
	);

	const jsmPreviewableActions = previewableActions.filter((action) =>
		isJsmCreatableAction({ key: action?.key }),
	);

	// This Jira-specific resolveActions implementation should be refactored into a generic solution for use by other products.
	// Please deprecate this in favour for a generic implemention from the useResolveActions hook.
	// For further guidance, refer to the comments in the useResolveActions hook.
	const resolveActions = useCallback(
		(
			actionKey: string,
			actionResolution: MessageActionResolutions,
			invocationIds: string[],
			invocationIdToCreatedIssueKey?: Record<string, string>,
		) => {
			bulkResolveConversationUIActions({
				conversationId: conversationId ?? '',
				messageId,
				payloads: invocationIds.map((invocationId) => ({
					context: {
						key: actionKey,
						invocationId: invocationId,
						messageId: messageId,
						data: {
							...(invocationIdToCreatedIssueKey
								? { createdIssueKey: invocationIdToCreatedIssueKey[invocationId] }
								: {}),
						},
						resolution: {
							type: actionResolution,
						},
					},
				})),
				skipSuccessOptimisticAssistantResponse: true,
			});
		},
		[bulkResolveConversationUIActions, conversationId, messageId],
	);

	// We need some custom handling of the Jira actions here since we want to show a single card
	// for multiple actions.
	if (jiraPreviewableActions.length > 0) {
		if (fg('jira-aifc-migrate-action-renderer')) {
			// When this flag is on, we want to use the non-streaming action renderer.
			return [];
		}

		const {
			key: actionKey,
			status,
			invocationId,
			data,
		} = jiraPreviewableActions[0] as ActionWith<SmartCreatePayload>;

		const lastAssistantCreatableActionMessageId =
			getJiraLastAssistantCreatableActionMessageId(messages);
		return [
			<SmartCreatePreviewComponent
				title={data?.title}
				contentId={data?.contentId}
				actionKey={actionKey}
				messageId={messageId}
				emojiProvider={emojiProvider}
				stagingAreaEnabled={elements?.stagingAreaEnabled}
				fullScreen={fullScreen}
				conversationId={conversationId}
				status={status}
				cloudId={cloudId}
				shouldOpenPreviewInAtlassianHome={shouldOpenPreviewInAtlassianHome}
				isLastMessage={lastAssistantCreatableActionMessageId === messageId}
				invocationId={invocationId}
				resolveActions={resolveActions}
				product={product}
			/>,
		];
	}

	const renderedActions = previewableActions.map((action, index) => {
		return (
			<SmartCreatePreviewComponent
				key={action.invocationId}
				isFirstPreviewCard={index === 0}
				title={(action as ActionWith<SmartCreatePayload>)?.data?.title}
				contentId={(action as ActionWith<SmartCreatePayload>)?.data?.contentId}
				actionKey={action?.key}
				messageId={messageId}
				emojiProvider={emojiProvider}
				stagingAreaEnabled={elements?.stagingAreaEnabled}
				fullScreen={fullScreen}
				conversationId={conversationId}
				status={action.status}
				cloudId={cloudId}
				shouldOpenPreviewInAtlassianHome={shouldOpenPreviewInAtlassianHome}
				isLastMessage={lastAssistantMessageId === messageId}
				invocationId={action.invocationId}
				product={product}
				resolveAction={
					jsmPreviewableActions.length > 0 && fg('surveys-aifc') ? resolveAction : undefined
				}
			/>
		);
	});

	return renderedActions;
};
